/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import type { ReactElement } from 'react';
import { NmWishlistIcon } from '@/components/icons';
import { Link } from '@/components/link';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/providers/auth';
import { useTranslation } from 'react-i18next';
import { useBrand } from '@/lib/brand/use-brand';
import { HEADER_COMPOSITION } from '@/lib/brand/header-composition';

export default function WishlistIcon(): ReactElement {
    const session = useAuth();
    const { t } = useTranslation('header');
    const isAuthenticated = session?.userType === 'registered' && Boolean(session?.customerId);
    const wishlistLink = isAuthenticated ? '/account/wishlist' : '/wishlist';
    // Per-brand utility presentation — lib/brand/header-composition.ts. On BG
    // the desktop control is the WORD "Wishlist" with no glyph
    // (bg-home-d-1280/App.tsx:99 — an <a> whose only content is that text);
    // the glyph stays for BG mobile and is untouched on NM, where this suffix
    // is the empty string and `asText` is false, so NM renders exactly the DOM
    // and class strings it did before.
    // Template strings, not `cn()`: NM's class strings are pinned byte-for-byte
    // in the storybook snapshots and tailwind-merge is free to normalise.
    const composition = HEADER_COMPOSITION[useBrand()];
    const asText = composition.desktopUtilities === 'text-links';
    const glyphGate = asText ? ` ${composition.mobileOnly}` : '';
    // AND the whole control leaves the mobile header. `glyphGate` above picks
    // the FORM this control takes at desktop; `wishlistMobileGate` decides
    // whether it is in the mobile header in the first place — two different
    // questions, which is why they are two fields.
    //
    // NEITHER BRAND CARRIES A WISHLIST AT MOBILE, so this gate is non-empty for
    // both and the composition test asserts that unconditionally.
    //   BG — `grep -ai 'wishlist|favourite|favorite|heart'` over the entire
    //   bg-home-m-375 and bg-home-m-440 captures returns zero hits, header or
    //   otherwise, while its DESKTOP header shows the word (bg-home-d-1280:99).
    //   NM — the same negative across six captures (home and search, at 375,
    //   390 and 440), each run with `sign ?in` / `shopping bag` as a positive
    //   control in the same command so an empty result cannot be a broken
    //   search. The class-name corroboration used to come from the
    //   nm-search-m-440 dump, which is barred; it is replaced by the OWNER'S
    //   OWN PIXELS, a stronger witness for a presence question anyway.
    //   `.reference/live-shots/nm-search-390.png` (sRGB, 2.0 px/CSS px), brand
    //   row at page y 62..105, ink runs across the full 390:
    //       x  10.5 ..  31.0   hamburger glyph (centred in live's 42px box)
    //       x 144.5 .. 244.0   wordmark, 100.0 wide
    //       x 318.0 .. 333.5   account
    //       x 361.5 .. 376.0   bag
    //   FOUR runs. The right group is exactly two controls and there is no
    //   third glyph for a heart to be. NM's desktop DOES have it
    //   (home-d-768 through home-d-1920 carry the `alt="my favorite icon"`
    //   15x15 <img>), which is why this is a breakpoint gate and not a brand
    //   boolean like `myOrders`.
    //
    // NM's row used to leave this empty on the strength of a `mobileUtilities`
    // value no capture supported — see MobileUtilityPresentation. The heart
    // shipped at 375/390/440 for as long as that value stood.

    return (
        // Rendered on ALL breakpoints. The 2026-08-16 mobile harvest shows the
        // wishlist icon in the live 375px closed header (x=304, between sign-in
        // and cart — .reference/harvest/ours-mobile-header-anatomy.json
        // missingVsLive). The cluster fits now that the hamburger moved to the
        // LEFT corner (P2 mobile recomposition), so the earlier desktop-only
        // overflow rationale no longer applies.
        // Icons match live rects; framework Button padding stripped — divergences
        // from live are flagged to the owner, never rationalized (see handoff rule 8c).
        // Live wishlist glyph is 15x15; its "28px side paddings" exist BETWEEN
        // icons and land as the icon row's gap token, never padding inside this
        // button. Focus-visible ring stays functional (it outsets). Mobile (<md)
        // keeps its compact px-1 cluster.
        <Button
            variant="ghost"
            className={`inline-flex cursor-pointer px-1 md:h-(--header-icon-box-height) md:px-(--header-icon-box-pad-x) md:has-[>svg]:px-(--header-icon-box-pad-x) md:py-(--header-icon-box-pad-y) hover:bg-transparent hover:opacity-50 transition-opacity ${composition.wishlistMobileGate}`.trimEnd()}
            asChild>
            <Link to={wishlistLink} aria-label={t('wishlist')}>
                {/* 3c blast-1 (les-blast-1-header-1280.md "Wishlist img"): live
                    glyph is 15x15 — md+ takes the token
                    (--header-icon-glyph-wishlist); mobile keeps 20px. */}
                <NmWishlistIcon className={`size-5 md:size-(--header-icon-glyph-wishlist)${glyphGate}`} />
                {asText && <span className={composition.desktopInline}>{t('wishlist')}</span>}
            </Link>
        </Button>
    );
}
